import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Tooltip } from '#src/atoms/tooltip/Tooltip/index.tsx';
import { LabelXS } from '#src/atoms/typography/index.tsx';
import { useApplyJonumRestriction } from '#src/hooks/useApplyJonumRestriction.ts';

const Trigger = styled(Tooltip)`
  cursor: help;
  /* Hoist above any LinkOverlay::before so the whole amount is hoverable. */
  position: relative;
  z-index: 1;
  /* If a parent paints text via background-clip: text (gold gradient titles,
   * etc.), inheriting color: transparent would make the wrapped amount
   * invisible. Re-inherit the gradient setup so the clip applies here too. */
  background-image: inherit;
  background-clip: inherit;
  -webkit-background-clip: inherit;
  /* Asterisk via pseudo-element so it doesn't leak into textContent. */
  &.with-asterisk::after {
    content: '*';
  }
`;

type Variant =
  | { variant: 'monetary' }
  | { variant: 'conversionCredit'; percentageDiscount?: number };

type Props = {
  children: ReactNode;
  /**
   * Whether the wrapper renders its own asterisk after the children.
   * Set to false when the call site already shows a literal "*" (e.g. as a
   * footnote anchor) — the trigger keeps working but the visual indicator
   * is owned by the parent to avoid a "**" pile-up.
   */
  withAsterisk?: boolean;
} & Variant;

const disclaimerLines = (props: Variant): ReactNode[] => {
  if (props.variant === 'monetary') {
    return [
      <FormattedMessage
        key="reference"
        defaultMessage="Rewards are displayed in EUR, USD or GBP equivalent for reference and paid in ETH or SOL."
      />,
      <FormattedMessage
        key="capped"
        defaultMessage="Individual rewards may be capped. Additional conditions apply."
      />,
    ];
  }
  return [
    props.percentageDiscount ? (
      <FormattedMessage
        key="discount"
        defaultMessage="Market credits provide a {percent} discount up to the advertised amount."
        values={{
          percent: (
            <FormattedNumber value={props.percentageDiscount} style="percent" />
          ),
        }}
      />
    ) : (
      <FormattedMessage
        key="discount"
        defaultMessage="Market credits provide a discount up to the advertised amount."
      />
    ),
    <FormattedMessage
      key="conditions"
      defaultMessage="Applicable on Auctions or Instant Buy only.Additional conditions apply."
    />,
  ];
};

const getTitle = (props: Variant) => (
  <Vertical gap={0}>
    {disclaimerLines(props).map(line => (
      <span key={(line as { key: string }).key}>{line}</span>
    ))}
  </Vertical>
);

const Note = styled(LabelXS)`
  display: flex;
  flex-direction: column;
  text-align: center;
`;

export const MonetaryRewardDisclaimerNote = (props: Variant) => {
  const applyJonumRestriction = useApplyJonumRestriction();
  if (!applyJonumRestriction) return null;
  return (
    <Note color="var(--c-nd-600)">
      {disclaimerLines(props).map((line, index) => (
        <span key={(line as { key: string }).key}>
          {index === 0 ? '* ' : ''}
          {line}
        </span>
      ))}
    </Note>
  );
};

export const MonetaryRewardDisclaimer = (props: Props) => {
  const { children, withAsterisk = true } = props;
  const applyJonumRestriction = useApplyJonumRestriction();
  if (!applyJonumRestriction) return <>{children}</>;
  return (
    <Trigger
      title={getTitle(props)}
      placement="top"
      className={withAsterisk ? 'with-asterisk' : undefined}
      // Showing the disclaimer is a legal prerequisite, and on touch there is
      // no hover fallback. The amount is usually inside a clickable, so a tap
      // must open the tooltip rather than bubble to a button onClick or
      // trigger anchor navigation underneath.
      onClick={event => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {children}
    </Trigger>
  );
};
